import { Classes, Colors } from "@blueprintjs/core";

import { Box } from "@/components/layout/flexbox";
import { Paging } from "@/components/pages_logged_in/workspace-admin/Paging";
import { FullRoleplay, PaginatedRoleplay } from "@/types/roleplays";

import { useRoleplayListQuery } from "./hooks";
import { RoleplayList } from "./RoleplayList";
import { RoleplayListHeader } from "./RoleplayListHeader";

const FETCH_LIMIT = 10;

interface RoleplaysListProps {
  isLoadingPage?: boolean;
  includeOwnerData?: boolean;
  onChoose?: (roleplay: PaginatedRoleplay) => void;
  showProgress?: boolean;
}

export function RoleplaysList({
  isLoadingPage = false,
  onChoose,
  showProgress = true,
  includeOwnerData = false,
}: RoleplaysListProps) {
  const {
    roleplays,
    totalRecords,
    loading: loadingData,
    refetchRoleplays,
    setSelectedOwner,
    setSelectedTags,
    setOrder,
    setSelectedScopes,
    setCurrentPage,
    selectedScopes,
    selectedOwner,
    selectedTags,
    currentPage,
    error,
    setSearchQuery,
    order,
  } = useRoleplayListQuery({ includeOwnerData, skip: isLoadingPage });
  const loading = isLoadingPage || loadingData;
  const firstLoading = !roleplays ? loading : false;
  const loadingBody = (
    <Box>
      {Array.from({ length: 10 }, (_, index) => (
        <Box
          key={index}
          height={100}
          className={Classes.SKELETON}
          mt={index === 0 ? 0 : 24}
        />
      ))}
    </Box>
  );

  return (
    <Box>
      <RoleplayListHeader
        selectedScopes={selectedScopes}
        selectedOwner={selectedOwner}
        selectedTags={selectedTags}
        selectedCollectionOrder={order}
        onChangeScope={(scopes) => {
          setSelectedScopes(scopes);
          setCurrentPage(1);
        }}
        onSearch={(query) => {
          setSearchQuery(query ? query.trim() : null);
          setCurrentPage(1);
        }}
        onOwnerChange={(owner) => {
          setSelectedOwner(owner);
          setCurrentPage(1);
        }}
        loading={firstLoading}
        onCreateCollectionFinished={() => {
          refetchRoleplays();
        }}
        allowAdd={true}
        onCollectionOrderChange={(order) => {
          setOrder(order);
          setCurrentPage(1);
        }}
        onTagsChange={(tagIds) => {
          setSelectedTags(tagIds);
          setCurrentPage(1);
        }}
      />
      <RoleplayList
        roleplays={roleplays as FullRoleplay[]}
        loading={firstLoading}
        viewMode={"list"}
        onChoose={onChoose}
        showActionBar={!!onChoose}
        showProgress={showProgress}
        refetchRoleplays={refetchRoleplays}
      />
      {!loading && roleplays?.length === 0 && (
        <Box my={3} textAlign={"center"}>
          No roleplays found
        </Box>
      )}
      {!loading && error && (
        <Box my={3} textAlign={"center"} color={Colors.RED3}>
          {error}
        </Box>
      )}
      {loading && !firstLoading ? loadingBody : null}
      <Paging
        style={{
          background: Colors.WHITE,
          padding: 10,
          marginTop: 20,
        }}
        currentPage={currentPage}
        pageSize={FETCH_LIMIT}
        setCurrentPage={(index) => {
          setCurrentPage(index);
        }}
        totalRecords={totalRecords}
        isSticky={true}
      />
    </Box>
  );
}
